/**
 * ADMIN → TEXT DESK — the Monday-morning view.
 *
 * Anthony: "Pending approvals with Approve and Refuse buttons, today's log
 * of every text and what the desk did with it, the on/off switch and the
 * daily count. Everything works by text today; this is the Monday-morning
 * view."
 *
 * Nothing here is a second way of doing anything. Approve presses the same
 * `settle()` the poller runs when he texts Y1. The switch is the same
 * `automations` row the guard reads. The log is the same `desk_requests`
 * table the desk writes as it answers. A screen that had its own logic
 * would be the second opinion this system was rebuilt to get rid of.
 *
 * READ UNDER THE CALLER'S OWN SESSION. The desk tables carry a boss-only
 * select policy, so a technician who types the URL sees the refusal and
 * not the shop's private traffic.
 */

import { db, api } from "../data/client.ts";
import { esc, ago, mount, since } from "../ui/dom.ts";
import { icon } from "../ui/icons.ts";
import type { Role } from "../domain/permissions.ts";
import { denied } from "./admin.ts";
import { shopDayStartIso } from "../ui/shopclock.ts";
import { shopWords } from "../ui/words.ts";

const BOSS: Role[] = ["admin", "supervisor"];

type Automation = { key: string; enabled: boolean; daily_cap: number | null; floor: string | null; settings: Record<string, unknown> };
type Approval = {
  id: string; code: number; summary: string; status: string; created_at: string; decided_at: string | null;
  decision_text: string | null; requested_by: string | null; approver_id: string;
  action: { args?: { job_number?: string; requester_name?: string } };
};
type Request = {
  id: string; body: string; handled_by: string | null; outcome: string | null; created_at: string; handled_at: string | null;
  cost_tokens: number | null;
  desk_threads: { phone10: string; profiles: { full_name: string; role: string } | null } | null;
};

const HANDLED: Record<string, { label: string; tone: "" | "warn" | "bad" | "good" }> = {
  "fast:address": { label: "lookup", tone: "" },
  "fast:help": { label: "help", tone: "" },
  "fast:ack": { label: "no reply", tone: "" },
  model: { label: "answered", tone: "" },
  "asked-owner": { label: "asked the owner", tone: "warn" },
  approval: { label: "decision", tone: "good" },
  referred: { label: "logged for the owner", tone: "warn" },
  photo: { label: "photo filed", tone: "good" },
  "photo:held": { label: "photo waiting for a job", tone: "warn" },
  stuck: { label: "dropped", tone: "bad" },
  dev: { label: "dev line", tone: "" },
  "ignored:collision": { label: "number on two records", tone: "bad" },
  "ignored:dev-not-owner": { label: "dev line, not the owner", tone: "warn" },
};
const pillFor = (h: string | null) => {
  if (!h) return `<span class="pill bad">unanswered</span>`;
  const k = HANDLED[h] ?? (h.startsWith("dev:D") ? { label: `dev request ${h.slice(4)}`, tone: "good" as const } : { label: h, tone: "" as const });
  return `<span class="pill ${k.tone}">${esc(k.label)}</span>`;
};

let filter: "all" | "needs" | "dev" = "all";

/** The few styles only this page needs, kept with the page rather than in the shared sheet. */
const STYLE = `<style id="desk-style">
  .pill.good{background:color-mix(in srgb,var(--green) 16%,transparent);color:var(--green2)}
  .pill.warn{background:color-mix(in srgb,var(--amber) 22%,transparent);color:#7a5200}
  .pill.bad{background:color-mix(in srgb,var(--red) 14%,transparent);color:var(--red)}
  .desk-decide{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:8px}
  .desk-decide .input{flex:1;min-width:200px;border:1px solid var(--line);border-radius:8px;padding:7px 10px;font:inherit;font-size:13.5px;background:var(--card);color:var(--ink)}
</style>`;

export async function deskPage(role: Role = "viewer"): Promise<void> {
  if (!BOSS.includes(role)) return denied("Text Desk");

  mount(`${STYLE}<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Text Desk</h1>
      <p>What the crew texted the shop line, what the desk did with it, and what is waiting on you.
         A button here does exactly what a Y1 or N1 by text does.</p></header>
    <div id="desk-switch"><div class="loading">Reading&hellip;</div></div>
    <div id="desk-waiting"></div>
    <div id="desk-log"></div>
    <div id="desk-decided"></div>
  </div>`);

  await Promise.all([renderSwitch(), renderWaiting(), renderLog(), renderDecided()]);
}

/* ── the switch and the count ─────────────────────────────────────── */

async function renderSwitch(): Promise<void> {
  const host = document.getElementById("desk-switch");
  if (!host) return;
  try {
    const sb = await db();
    const { data, error } = await sb.from("automations")
      .select("key,enabled,daily_cap,floor,settings").in("key", ["text_desk", "text_desk_customer"]);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as Automation[];
    // Named for what it is, not for when it starts. `since` is the helper in
    // ui/dom.ts and a local by that name shadows it inside this function.
    const dayStart = shopDayStartIso();
    const counts: Record<string, number> = {};
    for (const a of rows) {
      const { count } = await sb.from("comm_events").select("id", { count: "exact", head: true })
        .eq("direction", "outbound").gte("occurred_at", dayStart).eq("raw->>automation", a.key);
      counts[a.key] = count ?? 0;
    }
    const desk = rows.find((a) => a.key === "text_desk");
    const cust = rows.find((a) => a.key === "text_desk_customer");
    const line = (a: Automation | undefined, label: string, what: string) => a ? `
      <div class="row">
        <span class="rowicon">${icon("comms")}</span>
        <div class="row-main">
          <div class="row-title"><span class="num">${esc(label)}</span>
            <span class="onoff ${a.enabled ? "is-on" : "is-off"}">${
              a.enabled ? "ON" : "OFF"}</span></div>
          <div class="row-sub">${esc(what)}</div>
          <div class="row-note">${counts[a.key] ?? 0} sent today${a.daily_cap ? ` of ${a.daily_cap}` : ""}${
            a.floor ? ` &middot; on since ${esc(since(a.floor))}` : ""}</div>
        </div>
        <div class="row-side">
          <button class="btn sm ${a.enabled ? "danger" : "primary"}" data-switch="${esc(a.key)}"
                  data-to="${a.enabled ? "off" : "on"}"
                  aria-label="${a.enabled ? `Switch ${esc(label)} off` : `Switch ${esc(label)} on`}">
            ${a.enabled ? "Switch off" : "Switch on"}</button>
        </div>
      </div>` : "";
    host.innerHTML = `<div class="card">
      <div class="card-head">The switch</div>
      ${line(desk, "Shop line, 951-629-2224", "Answers staff. Off means nobody gets a reply and nothing is lost: texts wait and are answered when it comes back on.")}
      ${line(cust, "Customer texts on a staff member's word", "The relays a technician or the desk asks for. Customer hours and the no-contact list apply.")}
      <div id="switch-msg"></div>
    </div>`;

    host.querySelectorAll<HTMLButtonElement>("button[data-switch]").forEach((b) => b.addEventListener("click", async () => {
      const key = b.dataset["switch"] ?? "", to = b.dataset["to"] === "on";
      if (!to && !confirm(`Switch ${key === "text_desk" ? "the shop line" : "customer texts"} off? Staff texts will wait unanswered until it is back on.`)) return;
      const was = b.textContent ?? "";
      b.disabled = true;
      b.textContent = to ? "Switching on…" : "Switching off…";
      document.getElementById("switch-msg")!.innerHTML = "";
      // Turning on sets the floor to now, so nothing older is ever chased.
      const patch = to ? { enabled: true, floor: new Date().toISOString() } : { enabled: false };
      const { error } = await sb.from("automations").update(patch).eq("key", key);
      if (error) {
        // On the card, where the thing that failed is. A browser dialog can
        // land behind the app on a phone, and then the switch simply looks
        // like it did nothing.
        b.disabled = false; b.textContent = was;
        document.getElementById("switch-msg")!.innerHTML =
          `<p class="msg bad">That did not switch: ${esc(error.message)}</p>`;
        return;
      }
      await renderSwitch();
    }));
  } catch (e) {
    host.innerHTML = `<div class="card"><div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}

/* ── waiting on you ───────────────────────────────────────────────── */

async function renderWaiting(): Promise<void> {
  const host = document.getElementById("desk-waiting");
  if (!host) return;
  try {
    const sb = await db();
    const { data, error } = await sb.from("desk_approvals")
      .select("id,code,summary,status,created_at,action,requested_by,approver_id")
      .eq("status", "pending").order("created_at", { ascending: true });
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as Approval[];
    host.innerHTML = `<div class="card">
      <div class="card-head${rows.length ? " bad" : ""}">Waiting on you &middot; ${rows.length}</div>
      ${rows.length ? rows.map((ap) => `
        <div class="row" data-approval="${esc(ap.id)}">
          <span class="rowicon">${icon("authorizations")}</span>
          <div class="row-main">
            <div class="row-title"><span class="num">Y${ap.code} / N${ap.code}</span>
              <span class="pill warn">${esc(ap.action?.args?.job_number ?? "")}</span></div>
            <div class="row-sub">${esc(ap.summary)}</div>
            <div class="row-note">Asked ${esc(since(ap.created_at))} by ${esc(ap.action?.args?.requester_name ?? "somebody")}.
              A condition typed here goes to them in your words.</div>
            <div class="desk-decide">
              <input class="input" data-note maxlength="300">
              <button class="btn sm primary" data-yes="1">Approve</button>
              <button class="btn sm" data-yes="0">Refuse</button>
            </div>
          </div>
        </div>`).join("")
      : `<div class="empty">Nothing waiting. A blocked close ${esc(shopWords().caseFile ? "anyone on the team" : "a technician")} asks about lands here and on your phone at the same time.</div>`}
    </div>`;

    host.querySelectorAll<HTMLElement>("[data-approval]").forEach((row) => {
      const id = row.dataset["approval"] ?? "";
      row.querySelectorAll<HTMLButtonElement>("button[data-yes]").forEach((b) => b.addEventListener("click", async () => {
        const yes = b.dataset["yes"] === "1";
        const note = row.querySelector<HTMLInputElement>("input[data-note]")?.value.trim() ?? "";
        if (!confirm(yes ? "Close it, with your name on the override?" : "Refuse, and tell them to get what is missing?")) return;
        row.querySelectorAll("button").forEach((x) => (x.disabled = true));
        try {
          const { data: { session } } = await sb.auth.getSession();
          /* api(), not a bare path: inside the app shell "/api/..."
             resolves against the phone and this button does nothing. */
          const res = await fetch(api("/api/desk/decide"), {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token ?? ""}` },
            body: JSON.stringify({ approval_id: id, yes, note }),
          });
          const out = await res.json() as { ok?: boolean; outcome?: string; error?: string };
          if (!res.ok) throw new Error(out.error ?? "That did not go.");
          await Promise.all([renderWaiting(), renderDecided(), renderLog()]);
        } catch (e) {
          alert(e instanceof Error ? e.message : String(e));
          row.querySelectorAll("button").forEach((x) => (x.disabled = false));
        }
      }));
    });
  } catch (e) {
    host.innerHTML = `<div class="card"><div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}

/* ── today's log ──────────────────────────────────────────────────── */

async function renderLog(): Promise<void> {
  const host = document.getElementById("desk-log");
  if (!host) return;
  try {
    const sb = await db();
    const { data, error } = await sb.from("desk_requests")
      .select("id,body,handled_by,outcome,created_at,handled_at,cost_tokens,desk_threads(phone10,profiles(full_name,role))")
      .gte("created_at", shopDayStartIso()).order("created_at", { ascending: false }).limit(300);
    if (error) throw new Error(error.message);
    const all = (data ?? []) as unknown as Request[];
    const rows = all.filter((r) =>
      filter === "all" ? true
      : filter === "dev" ? (r.handled_by ?? "").startsWith("dev")
      : ["referred", "stuck", "photo:held", "ignored:collision"].includes(r.handled_by ?? "") || !r.handled_by);
    const who = (r: Request) => r.desk_threads?.profiles?.full_name
      ?? (r.desk_threads?.phone10 ? `Shop phone ending ${r.desk_threads.phone10.slice(-4)}` : "unknown");

    host.innerHTML = `<div class="card">
      <div class="card-head">Today &middot; ${all.length} text${all.length === 1 ? "" : "s"}</div>
      <div class="tabs wide" style="margin:10px var(--pad)">
        ${(["all", "needs", "dev"] as const).map((k) => `<button class="tab ${filter === k ? "on" : ""}" data-f="${k}">${
          k === "all" ? "Everything" : k === "needs" ? "Needs a person" : "Dev line"}</button>`).join("")}
      </div>
      ${rows.length ? rows.map((r) => `
        <div class="row">
          <span class="rowicon">${icon(r.handled_by?.startsWith("dev") ? "settings" : "comms")}</span>
          <div class="row-main">
            <div class="row-title"><span class="num">${esc(who(r))}</span>
              ${r.desk_threads?.profiles?.role ? `<span class="pill">${esc(r.desk_threads.profiles.role)}</span>` : ""}
              ${pillFor(r.handled_by)}</div>
            <div class="row-sub">${esc(r.body || "(a photo)")}</div>
            ${r.outcome ? `<div class="row-note">&rarr; ${esc(r.outcome.length > 220 ? r.outcome.slice(0, 217) + "…" : r.outcome)}</div>` : ""}
          </div>
          <div class="row-side"><div class="when">${esc(since(r.created_at))}</div></div>
        </div>`).join("")
      : `<div class="empty">${filter === "all" ? "Nothing texted the desk today." : "Nothing in this view today."}</div>`}
    </div>`;
    host.querySelectorAll<HTMLButtonElement>("button[data-f]").forEach((b) => b.addEventListener("click", () => {
      filter = (b.dataset["f"] as typeof filter) ?? "all"; void renderLog();
    }));
  } catch (e) {
    host.innerHTML = `<div class="card"><div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}

/* ── decided lately ───────────────────────────────────────────────── */

async function renderDecided(): Promise<void> {
  const host = document.getElementById("desk-decided");
  if (!host) return;
  try {
    const sb = await db();
    const { data, error } = await sb.from("desk_approvals")
      .select("id,code,summary,status,created_at,decided_at,decision_text,action")
      .neq("status", "pending").order("created_at", { ascending: false }).limit(12);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as Approval[];
    if (!rows.length) { host.innerHTML = ""; return; }
    host.innerHTML = `<div class="card">
      <div class="card-head">Decided lately</div>
      ${rows.map((ap) => `
        <div class="row">
          <span class="rowicon">${icon("authorizations")}</span>
          <div class="row-main">
            <div class="row-title"><span class="num">${esc(ap.action?.args?.job_number ?? "")}</span>
              <span class="pill ${ap.status === "refused" ? "bad" : ap.status === "expired" ? "warn" : "good"}">${esc(ap.status)}</span></div>
            <div class="row-sub">${esc(ap.summary)}</div>
            ${ap.decision_text ? `<div class="row-note">&ldquo;${esc(ap.decision_text)}&rdquo;</div>` : ""}
          </div>
          <div class="row-side"><div class="when">${esc(since(ap.decided_at ?? ap.created_at))}</div></div>
        </div>`).join("")}
    </div>`;
  } catch (e) {
    host.innerHTML = `<div class="card"><div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}
